import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import type { DocChatArtifactEvent } from 'features/knowledgeDocumentViewer/types'
import { FileText, PanelRightOpen } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { artifactCardTitle, openItemsLabel } from './artifactText'

const CardRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.75rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  background: var(--card);
`

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;

  > svg {
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
  }
`

/** Timeline card for a drafted or updated document with Open actions. */
export function ArtifactCard({
  event,
  onOpen,
  onOpenInTab,
  actionsDisabled = false,
  variant = 'chat',
}: {
  actionsDisabled?: boolean
  event: DocChatArtifactEvent
  onOpen: (event: DocChatArtifactEvent) => void
  onOpenInTab: (event: DocChatArtifactEvent) => void
  variant?: 'chat' | 'timeline'
}): React.ReactElement {
  useLingui()
  const title = artifactCardTitle(event)
  const openItems = openItemsLabel(event.missing_count)

  if (variant === 'timeline') {
    return (
      <Box display='flex' align='center' gap='sm' minWidth='none'>
        <Text as='span' variant='body' color='muted'>
          {title}
        </Text>
        <Button
          type='button'
          variant='outline'
          size='xs'
          disabled={actionsDisabled}
          onClick={() => onOpen(event)}>
          <Trans>Open</Trans>
        </Button>
      </Box>
    )
  }

  return (
    <CardRoot data-testid='artifact-card'>
      <CardTitleRow>
        <FileText />
        <Text as='span' variant='label'>
          {title}
        </Text>
      </CardTitleRow>
      {openItems ? (
        <Text as='p' variant='caption' color='muted'>
          {openItems}
        </Text>
      ) : null}
      <Box display='flex' gap='xs' wrap='wrap'>
        <Button
          type='button'
          size='sm'
          disabled={actionsDisabled}
          onClick={() => onOpen(event)}>
          <Trans>Open</Trans>
        </Button>
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={actionsDisabled}
          onClick={() => onOpenInTab(event)}>
          <PanelRightOpen data-icon='inline-start' />
          <Trans>Open in tab</Trans>
        </Button>
      </Box>
    </CardRoot>
  )
}
